import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { toggleSidebar } from 'actions/app';
import clsx from 'clsx';
import Text from 'UI/components/atoms/Text';
import TitleLabel from 'UI/components/atoms/TitleLabel';

import { useStyles } from './styles';

/** @typedef {import('types/app').ContentPageLayoutProps} ContentPageLayoutProps */

/** @typedef {import('types/app').TitleLabelProps} TitleLabelProps */

/** @typedef {import('@material-ui/core').GridProps} GridProps */

/**
 * @param {Object} props
 * @param {Object} props.customTitleGridConfig
 * @param {GridProps} [props.pageHeadlineGrid]
 * @param {TitleLabelProps} props.titleLabelProps
 * @param {undefined} props.actionsBar
 * @param {Object} props.actionsBarConfig
 * @param {string} props.text
 * @param {string} [props.sectionName]
 */
export const PageHeadline = ({
  customTitleGridConfig,
  titleLabelProps,
  actionsBar,
  text,
  actionsBarConfig,
  pageHeadlineGrid,
  sectionName
}) => {
  const isWithoutBackNavigation = !titleLabelProps?.backNavigation;

  const classes = useStyles({ isWithoutBackNavigation });

  return (
    <Grid container justifyContent="space-between" {...pageHeadlineGrid}>
      <Grid item {...customTitleGridConfig}>
        {!titleLabelProps?.loading && sectionName && (
          <span className={classes.sectionNameWrapper}>
            <Text
              variant="overline"
              text={sectionName.toUpperCase()}
              className={classes.sectionName}
            />
          </span>
        )}
        <TitleLabel
          text={text}
          gridLoadingProps={actionsBar && { md: 6 }}
          backNavigationProps={{ className: classes.backNavigation }}
          {...titleLabelProps}
        />
      </Grid>
      {actionsBar && (
        <Grid item xs={12} sm={6} lg={3} container justifyContent="flex-end" {...actionsBarConfig}>
          {actionsBar}
        </Grid>
      )}
    </Grid>
  );
};

PageHeadline.defaultProps = {
  sectionName: '',
  pageHeadlineGrid: {},
  customTitleGridConfig: {
    xs: 12,
    sm: 6,
    lg: 6
  }
};

export const ContentPageLayoutModes = {
  Main: 'mainLayout',
  Custom: 'customLayout'
};

/**
 * @param {ContentPageLayoutProps} props
 */
const ContentPageLayout = ({
  actionsBar,
  actionsBarConfig = null,
  children,
  className,
  customTitleGridConfig = null,
  height,
  layoutClassName,
  mode = ContentPageLayoutModes.Main,
  PageHeadlineProps = {},
  sectionName = '',
  statusBar,
  text = '',
  titleLabelProps = null,
  ...rest
}) => {
  const { isSidebarOpen } = useSelector(({ app }) => app.ui);
  const dispatch = useDispatch();
  const isSmallViewPort = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const classes = useStyles({ mode, height, isSidebarOpen });

  const gridModes = {
    mainLayout: {
      xs: 12,
      sm: 6,
      lg: 6
    },
    simplyTitle: {
      xs: 12
    },
    custom: customTitleGridConfig
  };

  const titleGrid = actionsBar ? gridModes[mode] : gridModes.simplyTitle;

  const layoutModes = {
    mainLayout: (
      <>
        {statusBar}
        <div className={clsx(classes.mainLayout, layoutClassName)}>
          <PageHeadline
            actionsBar={actionsBar}
            actionsBarConfig={actionsBarConfig}
            customTitleGridConfig={gridModes.custom ?? titleGrid}
            pageHeadlineGrid={{ spacing: 4 }}
            sectionName={sectionName}
            text={text}
            titleLabelProps={titleLabelProps}
            {...PageHeadlineProps}
          />
          {children}
        </div>
      </>
    ),
    customLayout: children // useful when text prop isn't provided or needed
  };

  const handleMouseDown = () => {
    if (!isSmallViewPort || !isSidebarOpen) return;
    dispatch(toggleSidebar(false));
  };

  return (
    <div
      id="content_wrapper"
      onMouseDown={handleMouseDown}
      role="presentation"
      className={clsx(
        classes.wrapperPageContainer,
        className,
        !isSmallViewPort && classes.marginLeftLargeView
      )}
      {...rest}
    >
      {layoutModes[mode]}
    </div>
  );
};

export default ContentPageLayout;
